<script setup lang="ts">
import './picture.ts';

export interface Picture {
  sources: {
    srcset: { path: string; size?: string }[] | string;
    width: number;
    height: number;
    media?: string;
  }[];
  image: {
    src: string;
    srcset?: string;
    width: number;
    height: number;
  };
}

export interface PhxPictureContentProps {
  alt?: string;
  title?: string;
  image?: Picture['image'];
  imgClass?: string;
  lazy?: boolean;
  fade?: boolean;
  sources?: Picture['sources'];
}

const { lazy, image, fade = undefined } = defineProps<PhxPictureContentProps>();

const emit = defineEmits<(e: 'load', ev: Event) => void>();

function concatSrcSet(srcset: Record<string, string>[] | string = []): string {
  if (typeof srcset === 'string') {
    return srcset;
  }
  return srcset.map((entry) => (entry.size ? `${entry.path} ${entry.size}` : entry.path)).join(', ');
}

function handleLoaded(e: Event) {
  emit('load', e);
}
</script>

<template>
  <picture>
    <source
      v-for="source in sources"
      :key="source.media || concatSrcSet(source.srcset)"
      :media="source.media"
      :srcset="concatSrcSet(source.srcset)"
      :width="source.width"
      :height="source.height"
    />

    <img
      :class="[imgClass, { 'phxsg-picture--fade': fade ?? lazy }]"
      :alt="alt"
      :title="title"
      :src="image?.src"
      :srcset="image?.srcset"
      :loading="lazy ? 'lazy' : 'eager'"
      :width="image?.width"
      :height="image?.height"
      @load="handleLoaded"
    />
  </picture>
</template>
